import React from 'react';
import {
  Create,
  CreateButton,
  Datagrid,
  Edit,
  List,
  TextField,
  FunctionField,
  useNotify,
  useRedirect,
  useResourceContext,
  BulkDeleteButton,
  BulkExportButton,
  TopToolbar,
} from 'react-admin';

import { LeadGenCampaignForm } from './forms/lead-gen-campaigns';
import { ESTDateField } from './custom-ra-fields/est-date-field';
import { Chip } from './line-items';

// List Actions
const LeadGenCampaignsListActions = () => (
  <TopToolbar>
    <CreateButton />
  </TopToolbar>
);

// List Component
export const LeadGenCampaignsList = () => (
  <List
    actions={<LeadGenCampaignsListActions />}
    bulkActionButtons={
      <>
        <BulkExportButton />
        <BulkDeleteButton />
      </>
    }
  >
    <Datagrid rowClick="edit">
      <TextField source="id" />
      <TextField source="name" />
      <FunctionField
        source="targetLists"
        label="Target Lists"
        render={record => {
          // Try expanded list first (from typeMap expand), then connection nodes
          const lists = record?.targetListsList || record?.targetLists?.nodes || [];
          if (lists.length === 0) return <span style={{ color: '#999' }}>None</span>;
          return lists.map((tl: { id: number; name: string }) => tl.name).join(', ');
        }}
      />
      <ESTDateField source="startsAt" label="Start Date" />
      <ESTDateField source="endsAt" label="End Date" />
      <FunctionField
        source="isApproved"
        label="Status"
        render={record => <Chip isApproved={record?.isApproved} />}
      />
    </Datagrid>
  </List>
);

// Create Component
export const LeadGenCampaignCreate: React.FC = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  const resource = useResourceContext();

  const onSuccess = () => {
    notify('Saved new Lead Gen Campaign');
    redirect('list', resource);
  };

  return (
    <Create
      resource="leadGenCampaigns"
      transform={(data: any) => {
        const { targetListIdsList, ...rest } = data;
        return {
          ...rest,
          targetListIds: targetListIdsList || [],
        };
      }}
      mutationOptions={{ onSuccess }}
    >
      <LeadGenCampaignForm type="Create" />
    </Create>
  );
};

// Edit Component
export const LeadGenCampaignEdit: React.FC = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  const resource = useResourceContext();

  const onSuccess = () => {
    notify('Updated Lead Gen Campaign');
    redirect('list', resource);
  };

  const onError = (error: any) => {
    console.error('❌ LeadGenCampaignEdit mutation error:', error);
    notify(error?.message || 'Failed to update Lead Gen Campaign', { type: 'warning' });
  };

  return (
    <Edit
      mutationMode="pessimistic"
      transform={(data: any) => {
        const { targetListIdsList, targetListsList, ...rest } = data;
        return {
          ...rest,
          targetListIds: targetListIdsList || [],
        };
      }}
      mutationOptions={{ onSuccess, onError }}
    >
      <LeadGenCampaignForm type="Update" />
    </Edit>
  );
};

